import { lazy, Suspense, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AppShell } from "@/components/app-shell";
import { useAppData } from "@/data/data-context";
import { builderGuideEnabled } from "@/pages/build-guide-model";
import { CatalogPage } from "@/pages/catalog";

const AccessDeniedPage = lazy(() => import("@/pages/access").then((module) => ({ default: module.AccessDeniedPage })));
const NotFoundPage = lazy(() => import("@/pages/access").then((module) => ({ default: module.NotFoundPage })));
const AdminPage = lazy(() => import("@/pages/admin").then((module) => ({ default: module.AdminPage })));
const ActivityPage = lazy(() => import("@/pages/activity").then((module) => ({ default: module.ActivityPage })));
const CostsPage = lazy(() => import("@/pages/costs").then((module) => ({ default: module.CostsPage })));
const AppDetailPage = lazy(() => import("@/pages/app-detail").then((module) => ({ default: module.AppDetailPage })));
const MorePage = lazy(() => import("@/pages/more").then((module) => ({ default: module.MorePage })));
const ApiAccessPage = lazy(() => import("@/pages/api-access").then((module) => ({ default: module.ApiAccessPage })));
const BuildGuidePage = lazy(() => import("@/pages/build-guide").then((module) => ({ default: module.BuildGuidePage })));
const DemoEmbeddedApp = lazy(() => import("@/pages/demo-app").then((module) => ({ default: module.DemoEmbeddedApp })));
const loadLauncher = () => import("@/pages/launcher");
const LauncherPage = lazy(() => loadLauncher().then((module) => ({ default: module.LauncherPage })));
// Download the small launcher module while authentication/catalog data load.
void loadLauncher().catch(() => {});
const PrivacyPage = lazy(() => import("@/pages/legal").then((module) => ({ default: module.PrivacyPage })));
const TermsPage = lazy(() => import("@/pages/legal").then((module) => ({ default: module.TermsPage })));
const LoginPage = lazy(() => import("@/pages/login").then((module) => ({ default: module.LoginPage })));
const ManagePage = lazy(() => import("@/pages/manage").then((module) => ({ default: module.ManagePage })));
const NotificationsPage = lazy(() => import("@/pages/notifications").then((module) => ({ default: module.NotificationsPage })));
const SubmitPage = lazy(() => import("@/pages/submit").then((module) => ({ default: module.SubmitPage })));
const SubmitFeedbackPage = lazy(() => import("@/pages/feedback").then((module) => ({ default: module.SubmitFeedbackPage })));
const FeedbackBoardPage = lazy(() => import("@/pages/feedback").then((module) => ({ default: module.FeedbackBoardPage })));
const FeedbackDetailPage = lazy(() => import("@/pages/feedback").then((module) => ({ default: module.FeedbackDetailPage })));
const ProposeActionPage = lazy(() => import("@/pages/feedback").then((module) => ({ default: module.ProposeActionPage })));

function Protected({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const location = useLocation();
  const { isAuthenticated, isLoading, currentUser } = useAppData();
  if (isLoading) return <main className="grid min-h-screen place-items-center text-sm font-bold text-muted-foreground">Loading Cheesy Apps…</main>;
  if (!isAuthenticated) return <Navigate to="/login" state={{ returnTo: location.pathname + location.search + location.hash }} replace />;
  if (!currentUser || currentUser.access === "denied" || currentUser.status === "suspended") return <Navigate to="/access-denied" replace />;
  if (admin && currentUser?.role !== "admin") return <Navigate to="/apps" replace />;
  return children;
}

export function App() {
  return <Suspense fallback={<main className="grid min-h-screen place-items-center text-sm font-bold text-muted-foreground">Loading Cheesy Apps…</main>}><Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route path="/privacy" element={<PrivacyPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/access-denied" element={<AccessDeniedPage />} />
    <Route path="/demo-app" element={<DemoEmbeddedApp />} />
    <Route element={<Protected><AppShell /></Protected>}>
      <Route index element={<Navigate to="/apps" replace />} />
      <Route path="apps" element={<CatalogPage />} />
      <Route path="apps/:slug" element={<AppDetailPage />} />
      <Route path="apps/:slug/feedback" element={<SubmitFeedbackPage />} />
      <Route path="feedback" element={<FeedbackBoardPage />} />
      <Route path="feedback/new" element={<ProposeActionPage />} />
      <Route path="feedback/:itemId" element={<FeedbackDetailPage />} />
      <Route path="apps/:slug/launch" element={<LauncherPage />} />
      <Route path="build" element={builderGuideEnabled ? <BuildGuidePage /> : <Navigate to="/apps" replace />} />
      <Route path="submit" element={<SubmitPage />} />
      <Route path="manage/:slug" element={<ManagePage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="more" element={<MorePage />} />
      <Route path="settings/api-access" element={<ApiAccessPage />} />
      <Route path="admin" element={<Protected admin><AdminPage /></Protected>} />
      <Route path="admin/activity" element={<Protected admin><ActivityPage /></Protected>} />
      <Route path="admin/costs" element={<Protected admin><CostsPage /></Protected>} />
      <Route path="*" element={<NotFoundPage />} />
    </Route>
  </Routes></Suspense>;
}
